<template>
  <div class="mb-7 md:space-y-32">
    <slot
      :current-step
      name="sticky-bar"
      :selected-options
      :total-steps="funnel.length"
    />

    <template v-for="(step, index) in funnel" :key="step.id">
      <!-- Telco picker: placement is declared in funnel data (step.showTelcoPickerBefore) -->
      <PdpErrorBoundary>
        <ErrorOwners :owners="['bot-squad-partnerships-front']">
          <TelcoPlanPicker
            v-if="isPickerEligible && step.showTelcoPickerBefore"
            class="py-32 md:py-24"
            :is-picker-eligible
            :options="telcoOptions"
            :selected-partner-name
            @select-plan="telcoSelectPlan"
          />
        </ErrorOwners>
      </PdpErrorBoundary>

      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div
        ref="steps"
        :aria-label="
          typeof step.title === 'string' ? step.title : i18n(step.title)
        "
        class="py-32 md:py-24"
        @focus="() => handleFocusedStep(index + firstStepsList.length)"
        @mouseenter="() => handleFocusedStep(index + firstStepsList.length)"
        @mouseover="
          () =>
            /* ToDo: Fix when hovering the last step but proposal is the currentStep */ handleFocusedStep(
              index + firstStepsList.length,
            )
        "
        @touchstart="() => handleFocusedStep(index + firstStepsList.length)"
      >
        <template v-if="step.isTradeInStep">
          <!-- StepTradeIn handles its own errors internally via BuybackErrorBoundary -->
          <StepTradeIn
            v-if="isSwapEnabled && product.isTradeInEligible"
            :category="product.tracking.categoryName"
            :is-focused="isFocusedStep(index + firstStepsList.length)"
            :model="product.model"
            :price="priceWithoutSubsidies || undefined"
          />
        </template>

        <PdpErrorBoundary v-else-if="step.id === 'trade_in_au'">
          <ErrorOwners :owners="['bot-squad-apac-front']">
            <StepTradeInAU />
          </ErrorOwners>
        </PdpErrorBoundary>

        <StepKeyboard
          v-else-if="step.id === 'keyboard_type_language'"
          :first-picker-id
          :product
          :step
        />

        <Step
          v-else
          :first-picker-id
          :hide-guidance-card="step.id === 'battery' && hideBatteryGuidanceCard"
          :pickers
          :product
          :step
        >
          <template #title-grades>
            <RevLink
              v-if="step.guidance?.clickable"
              class="body-2-link relative bottom-2 block pl-12 md:hidden"
              variant="link"
              @click="onGuidanceGradesLinkClick"
            >
              {{ i18n(translations.conditionModalLinkSmallScreen) }}
            </RevLink>
          </template>

          <template #description-grades>
            <p class="body-2 mb-16 block md:hidden">
              {{ i18n(translations.conditionDescription) }}
            </p>
          </template>

          <template #description-mobile_plan>
            <PdpErrorBoundary>
              <ErrorOwners :owners="['bot-squad-partnerships-front']">
                <StepDescriptionMobilePlan :offers />
              </ErrorOwners>
            </PdpErrorBoundary>
          </template>

          <template #guidance-model_edition>
            <Gallery
              :allow-media-viewer="false"
              :allow-partners-images="false"
              class="mb-16 w-full"
              :images
              :should-preload-first-img="false"
              :tracking-product-model="product.model"
              tracking-zone="pp_step_model_edition_carousel"
            />
          </template>

          <template #guidance-color>
            <Gallery
              :allow-media-viewer="false"
              :allow-partners-images="false"
              class="mb-16 w-full"
              :images
              :should-preload-first-img="false"
              :tracking-product-model="product.model"
              tracking-zone="pp_step_color_carousel"
            />
          </template>

          <template #step-end-mobile_plan>
            <PdpErrorBoundary>
              <ErrorOwners :owners="['bot-squad-partnerships-front']">
                <StepEndMobilePlan :step :tracking-model="product.model" />
              </ErrorOwners>
            </PdpErrorBoundary>
          </template>

          <template #guidance-grades>
            <slot name="guidance-grades" :product :step>
              <ConditionGuidance
                :brand="product.brand"
                :content="step.sideContentV2?.content"
                :tracking-model="product.model"
              />
            </slot>
          </template>
        </Step>
      </div>
    </template>

    <ErrorOwners :owners="['bot-squad-partnerships-front']">
      <PromoCodeStep
        :category="product.tracking.categoryName"
        :model="product.model"
        :partner-promo-codes="product.includedServiceOffers.partnerPromoCodes"
      />
    </ErrorOwners>

    <Proposal
      v-if="!hasNoVariants && (funnel.length > 0 || funnel.length === 1)"
      ref="proposal"
      :is-focused="isProposalFocused"
      :mobile-plan="selectedMobilePlan"
      :price
      :price-without-subsidies
      :product
      :selected-color-code
      :selected-offer
      :selected-options
      :telco-selection="telcoCartSelection"
      :tracking="productTracking"
      :upgrade-picker-fields
      @focus="() => handleFocusedProposal()"
      @mouseenter="() => handleFocusedProposal()"
    >
      <template #details>
        <slot name="details" />
      </template>
    </Proposal>
    <Container innerClass="md:px-0">
      <div :class="isSqueezedBySidebar ? '' : 'md:flex md:justify-center'">
        <div
          v-if="!isSqueezedBySidebar"
          class="max-w-full md:relative md:mr-32 md:min-w-[337px] md:max-w-[498px] md:grow lg:mr-64"
        />
        <div
          :class="
            isSqueezedBySidebar
              ? 'w-full'
              : 'md:w-[360px] md:shrink-0 min-[900px]:w-[456px]'
          "
        >
          <ContainerAdditionalInfo />

          <ContainerVat v-if="hasNoVariants" class="my-32 md:my-56" />
        </div>
      </div>
    </Container>
  </div>

  <div :id="STICKY_BARS_PORTAL_TARGET" />

  <ClientOnly>
    <NewBatteryModal
      v-if="
        !(
          marketplace.market.countryCode === MarketCountryCode.US &&
          experiments['experiment.usNewBattery'] === 'withoutBatteryPicker'
        )
      "
      :brand="product.brand"
      :category-id="product.tracking.categoryId"
      :device-name="product.titles.raw"
    />
    <SimModal v-if="showSimModal" />
    <MacbookProcessorModal v-if="showMacbookProcessorModal" />
    <LaptopProcessorModal v-if="showLaptopProcessorModal" />
    <MobilePlanOffersDrawer v-if="showMobilePlanOffersDrawer" :offers />
    <ConditionDescriptionModal
      v-if="gradeStep"
      :brand="product.brand"
      :category-id="product.tracking.categoryId"
      :options="gradeStep.options"
    />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute, useState } from '#imports'
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { Price } from '@backmarket/http-api/src/api-models/Price'
import {
  type GetLegacyServicePickersResponse,
  type GetPickersResponse,
  hasItemExtraData,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import ErrorOwners from '@backmarket/nuxt-module-logger/components/ErrorOwners.vue'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { useIntersectionObserver } from '@vueuse/core'

import StepTradeIn from '~/scopes/buyback/swap/components/SwapStep/TradeInStep.vue'
import { useSwapEnabled } from '~/scopes/buyback/swap/composables/useSwapEnabled/useSwapEnabled'
import ContainerAdditionalInfo from '~/scopes/product-core/components/containers/ContainerCustomizationFunnel/components/ContainerAdditionalInfo/ContainerAdditionalInfo.vue'
import ContainerVat from '~/scopes/product-core/components/containers/ContainerVat/ContainerVat.vue'
import Container from '~/scopes/product-core/components/presentationals/Container/Container.vue'
import Gallery from '~/scopes/product-core/components/presentationals/Gallery/Gallery.vue'
import PdpErrorBoundary from '~/scopes/product-core/components/presentationals/PdpErrorBoundary/PdpErrorBoundary.vue'
import { useBatteryConfidenceExperiment } from '~/scopes/product-core/composables/useBatteryConfidenceExperiment/useBatteryConfidenceExperiment'
import { useProductTracking } from '~/scopes/product-core/composables/useFetch/useFetchProductTracking/composables/useProductTracking/useProductTracking'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import {
  MACBOOKS_CATEGORIES,
  MODAL_NAMES,
  ProductCategory,
  STICKY_BARS_PORTAL_TARGET,
} from '~/scopes/product-core/constants'
import TelcoPlanPicker from '~/scopes/product-core/pages/ProductDetailPage/us/components/presentationals/TelcoPlanPicker/TelcoPlanPicker.vue'
import { useTelcoPicker } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useTelcoPicker/useTelcoPicker'
import { useShoppingAssistantSqueezeOffset } from '~/scopes/shopping-assistant/composables/useShoppingAssistantSqueezeOffset'
import StepTradeInAU from '~/scopes/tradein-apac/components/CustomizationFunnelStep/StepAU.vue'

import translations from './CustomizationFunnel.translations'
import ConditionDescriptionModal from './components/ConditionDescriptionModal/ConditionDescriptionModal.vue'
import ConditionGuidance from './components/ConditionGuidance/ConditionGuidance.vue'
import MobilePlanOffersDrawer from './components/MobilePlanOffersDrawer/MobilePlanOffersDrawer.vue'
import NewBatteryModal from './components/NewBatteryModal/NewBatteryModal.vue'
import LaptopProcessorModal from './components/ProcessorModal/components/LaptopProcessorModal/LaptopProcessorModal.vue'
import MacbookProcessorModal from './components/ProcessorModal/components/MacbookProcessorModal/MacbookProcessorModal.vue'
import PromoCodeStep from './components/PromoCodeStep/PromoCodeStep.vue'
import Proposal from './components/Proposal/Proposal.vue'
import SimModal from './components/SimModal/SimModal.vue'
import Step from './components/Step/Step.vue'
import StepDescriptionMobilePlan from './components/StepDescriptionMobilePlan/StepDescriptionMobilePlan.vue'
import StepEndMobilePlan from './components/StepEndMobilePlan/StepEndMobilePlan.vue'
import StepKeyboard from './components/StepKeyboard/StepKeyboard.vue'
import { createCustomizationFunnelData } from './utils/createCustomizationFunnelData'
import type { Step as StepType } from './utils/types'

const props = withDefaults(
  defineProps<{
    selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
    pickers: GetPickersResponse
    pickersServices: GetLegacyServicePickersResponse['pickerGroups']
    isServicesPickersResponsePending: boolean
    price: Price
    priceWithoutSubsidies?: Price | null
    priceWithoutTradeIn?: Price | null
    product: GetProductResponse
    productTracking: ReturnType<typeof useProductTracking>
    selectedMobilePlan?: MobilePlanOffer
    upgradePickerFields?: NonNullable<
      GetPickersResponse['upgrade']
    >['pickerFields']
  }>(),
  {
    selectedMobilePlan: undefined,
    priceWithoutSubsidies: undefined,
    priceWithoutTradeIn: null,
    upgradePickerFields: undefined,
  },
)

const route = useRoute()
const i18n = useI18n()
const { trackClick } = useTracking()
const { getDiscountedPrice } = useBuybackOffer()

const {
  isPickerEligible,
  options: telcoOptions,
  selectedPartnerName,
  telcoCartSelection,
  selectPlan: telcoSelectPlan,
} = useTelcoPicker(() => props.product)
const proposal = useTemplateRef<HTMLDivElement>('proposal')
const proposalObserver = ref<ReturnType<typeof useIntersectionObserver>>()
const lastYPosition = ref(0)
const scrollDirection = ref<'up' | 'down'>()
const currentStep = useState('current-step', () => 0)
// For the 2 following lines, have to use shallowRef instead of useTemplateRefsList
// Please see https://github.com/BackMarket/front-apps/pull/11560#discussion_r2358972073
const steps = shallowRef<HTMLDivElement[] | null>([])
const firstStepsRefs = shallowRef<HTMLDivElement[] | null>([])
const stepsObserver = ref<ReturnType<typeof useIntersectionObserver>>()
const stepsIntersections = ref<Record<string, number>>({})
const { mobilePlan } = useUrlParams()
const experiments = useExperiments()
const marketplace = useMarketplace()
const { isSwapEnabled } = useSwapEnabled()
const { isControl: isBatteryConfidenceControl } =
  useBatteryConfidenceExperiment()

const hideBatteryGuidanceCard = computed(
  () =>
    !isBatteryConfidenceControl.value &&
    props.product.tracking.categoryId === ProductCategory.SMARTPHONE,
)

// The accordion column below is normally offset with an empty spacer div so
// it lines up under the pickers' right column — but that spacer's fixed
// min-width isn't squeeze-aware, so it pushes the accordion off-center when
// the assistant narrows the available width. Collapse to a single column
// (same as the mobile layout) when squeezed instead.
const { squeezeOffsetPx } = useShoppingAssistantSqueezeOffset()
const isSqueezedBySidebar = computed(() => squeezeOffsetPx.value > 0)

const firstStepsList = computed(() => {
  const gradeSteps = []

  if (firstStepsRefs.value) {
    gradeSteps.push(...firstStepsRefs.value)
  }

  return gradeSteps
})

const stepsConcat = computed(() => {
  const stepsRef = steps.value ? [...steps.value] : []

  return [...firstStepsList.value, ...stepsRef]
})

/**
 * List of steps that conform
 * the page funnel.
 */
const funnel = computed(() => {
  const funnelSteps = createCustomizationFunnelData(
    i18n,
    props.pickers,
    props.pickersServices,
    props.product,
    props.priceWithoutSubsidies,
    getDiscountedPrice(props.priceWithoutTradeIn),
    experiments,
  )

  if (
    marketplace.market.countryCode === MarketCountryCode.US &&
    experiments['experiment.usNewBattery'] === 'withoutBatteryPicker'
  ) {
    return funnelSteps.filter(({ id }) => id !== 'battery')
  }

  return funnelSteps
})

const hasNoVariants = computed(() => {
  return props.pickers.pickerGroups.length === 0
})

const firstPickerId = computed(() => {
  return props.pickers.pickerGroups[0]?.id
})

const gradeStep = computed(() => {
  return funnel.value.find(({ id }) => id === 'grades')
})

const selectedOptions = computed(() => {
  const variants = funnel.value
    ?.reduce<Array<string>>((acc, { options, type }) => {
      // We don't want to display in the sticky bar or the proposal the following steps:
      // - services
      if (type !== 'service') {
        const label =
          options?.find(({ selected }) => !!selected)?.label ?? undefined
        if (label) {
          acc.push(typeof label === 'object' ? i18n(label) : label)
        }
      }

      return acc
    }, [])
    .filter(Boolean)

  if (variants.length === 0) {
    return [props.product.titles.raw]
  }

  return variants
})

const selectedColorCode = computed(() => {
  return (
    funnel.value
      ?.find(({ id }) => id === 'color')
      ?.options?.find(({ selected }) => !!selected)?.color ?? undefined
  )
})

const offers = computed(() => {
  const mobilePlanItems = props.pickersServices?.find(
    ({ id }) => id === 'mobile_plan',
  )?.items

  if (!mobilePlanItems) return []

  return mobilePlanItems
    .filter(hasItemExtraData)
    .map(({ mobilePlanOffer }) => mobilePlanOffer)
})

const showSimModal = computed(() => {
  return props.pickers.pickerGroups?.some(({ id }) => id === 'dual_sim')
})
const showMobilePlanOffersDrawer = computed(() => {
  return props.pickersServices?.some(({ id }) => id === 'mobile_plan')
})
const showProcessorModal = computed(() => {
  return props.pickers.pickerGroups?.some(
    ({ id }) =>
      id === 'processor_type_and_speed' ||
      id === 'processor_type_and_graphic_card' ||
      id === 'processor',
  )
})

const showMacbookProcessorModal = computed(() => {
  return (
    showProcessorModal.value &&
    MACBOOKS_CATEGORIES.includes(props.product.tracking.categoryId)
  )
})

const showLaptopProcessorModal = computed(() => {
  return (
    showProcessorModal.value &&
    ProductCategory.LAPTOP === props.product.tracking.categoryId
  )
})

const images = computed(() => {
  return props.product.images.slice(0, 3)
})

function onGuidanceGradesLinkClick(guidance: StepType['guidance']) {
  openModal(MODAL_NAMES.CONDITION_COMPARISON)

  if (guidance?.tracking) {
    trackClick({
      name: guidance.tracking.name,
      zone: guidance.tracking.zone,
    })
  }
}

function isStepObserverInitialized() {
  return (
    stepsConcat.value &&
    stepsConcat.value.length > 0 &&
    stepsObserver.value?.isActive
  )
}

function handleFocusedStep(index: number) {
  if (isStepObserverInitialized()) {
    currentStep.value = index
  }
}

const isProposalObserverInitialized = computed(() => {
  return proposal.value && proposalObserver.value?.isActive
})

function handleFocusedProposal() {
  if (isProposalObserverInitialized.value && funnel.value) {
    currentStep.value = funnel.value.length
  }
}

const isProposalFocused = computed(() => {
  return isProposalObserverInitialized.value && funnel.value
    ? currentStep.value === funnel.value.length
    : true
})

function isFocusedStep(index: number) {
  return isStepObserverInitialized() ? currentStep.value === index : true
}

function addIntersectionObservers() {
  stepsObserver.value = useIntersectionObserver(
    stepsConcat,
    (entries) => {
      entries.forEach(({ target, intersectionRatio }) => {
        const index = stepsConcat.value.findIndex(
          (element) => element === target,
        )

        if (index == null) {
          return
        }

        stepsIntersections.value = {
          ...stepsIntersections.value,
          [index]: intersectionRatio,
        }
      })

      scrollDirection.value =
        lastYPosition.value > window.scrollY ? 'up' : 'down'
      lastYPosition.value = window.scrollY
    },
    {
      threshold: [0, 0.25, 0.5, 0.75, 1],
    },
  )

  proposalObserver.value = useIntersectionObserver(
    proposal.value,
    ([{ boundingClientRect, isIntersecting, intersectionRatio }]) => {
      // When more than 3/4 of a step is displaying, it became the focus state.
      // We remove the focus the previous step when it starts to disappear.
      if (
        isIntersecting &&
        parseFloat((Math.round(intersectionRatio * 4) / 4).toFixed(2)) >= 0.25
      ) {
        if (
          (lastYPosition.value > window.scrollY &&
            boundingClientRect.top < 0) ||
          (lastYPosition.value <= window.scrollY && boundingClientRect.top > 0)
        ) {
          handleFocusedProposal()
        }
      }

      scrollDirection.value =
        lastYPosition.value > window.scrollY ? 'up' : 'down'
      lastYPosition.value = window.scrollY
    },
    {
      threshold: [0, 0.25, 0.5, 0.75, 1],
    },
  )
}

function clearIntersectionObservers() {
  stepsIntersections.value = {}

  stepsObserver.value?.stop()
  stepsObserver.value = undefined

  proposalObserver.value?.stop()
  proposalObserver.value = undefined
}

onMounted(() => {
  addIntersectionObservers()
})

onUnmounted(() => {
  clearIntersectionObservers()
})

watch([funnel], () => {
  clearIntersectionObservers()
  addIntersectionObservers()
})

watch(stepsIntersections, () => {
  let intersections = Object.entries(stepsIntersections.value)

  if (!intersections.length) {
    return
  }

  if (scrollDirection.value === 'down') {
    intersections = intersections.reverse()
  }

  const step = intersections.reduce((acc, [index, intersectionRatio]) => {
    if (!acc) return [index, intersectionRatio]
    if (acc[1] < intersectionRatio) return [index, intersectionRatio]

    return acc
  })

  if (step) {
    const [index, intersectionRatio] = step
    const currentStepIndex = parseInt(index, 10)
    // only select currentStep, if the step is 100% visible
    // but skip this if the currentStep is the last step (Tada/Proposal)
    if (intersectionRatio === 1 && !isProposalFocused.value) {
      handleFocusedStep(currentStepIndex)
    }
  }
})

watch(
  [() => props.pickersServices],
  async () => {
    const areMobilePlanAvailable = funnel.value
      .find(({ id }) => id === 'mobile_plan')
      ?.options.slice(1)
      .every((o) => !o.available)

    if (areMobilePlanAvailable && mobilePlan.value) {
      await navigateTo({
        path: route.path,
        query: {
          ...route.query,
          mobilePlanOfferId: undefined,
        },
        hash: route.hash,
        replace: true,
      })
    }
  },
  { immediate: true },
)
</script>
